import * as React from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Camera, X, RotateCcw, Upload } from "lucide-react";
import { LoadingSpinner } from "@/components/ui/loading-spinner";
import { toast } from "@/components/ui/sonner";
import { useImageUpload } from "@/hooks/useImageUpload";
import { runOcr, extractLabelFields } from '@/lib/api';
import { usePackages } from '@/hooks/usePackages';
import { useState, useEffect } from "react";
import { supabase } from '@/integrations/supabase/client';

interface PhotoUploadModalProps {
  open: boolean;
  imageUrl: string | null;
  onClose: () => void;
  onSave: (newBase64: string) => void;
  itemId: string;
  enableOcr?: boolean;
}

const CAMERA_CONSTRAINTS = {
  width: { ideal: 1280 },
  height: { ideal: 720 }
};

const ALLOWED_FILE_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

const PhotoUploadModal = ({ open, imageUrl, onClose, onSave, itemId, enableOcr = false }: PhotoUploadModalProps) => {
  const [photoFile, setPhotoFile] = React.useState<File | null>(null);
  const [photoPreview, setPhotoPreview] = React.useState<string | null>(imageUrl);
  const [facingMode, setFacingMode] = React.useState<'environment' | 'user'>('environment');
  const [isInitialized, setIsInitialized] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [showCamera, setShowCamera] = React.useState(false);
  const { uploadImage, isUploading } = useImageUpload();
  const [isMobile] = React.useState(() => {
    if (typeof window === 'undefined') return false;
    const userAgent = navigator.userAgent || navigator.vendor;
    return /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i
      .test(userAgent.toLowerCase());
  });
  const [signedPhotoUrl, setSignedPhotoUrl] = useState<string | null>(null);
  
  const videoRef = React.useRef<HTMLVideoElement>(null);
  const streamRef = React.useRef<MediaStream | null>(null);
  const fileReaderRef = React.useRef<FileReader | null>(null);
  const { createPackage } = usePackages();

  const stopCamera = React.useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
      if (videoRef.current) {
        videoRef.current.srcObject = null;
      }
      setIsInitialized(false);
    }
  }, []);

  const startCamera = React.useCallback(async () => {
    try {
      setError(null);

      if (streamRef.current) {
        stopCamera();
      }

      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: { ...CAMERA_CONSTRAINTS, facingMode }
      });

      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
        await new Promise<void>((resolve) => {
          if (videoRef.current) {
            videoRef.current.onloadedmetadata = () => resolve();
          }
        });
        await videoRef.current.play();
      }

      streamRef.current = mediaStream;
      setIsInitialized(true);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to access camera';
      setError(errorMessage);
      toast.error(`Camera error: ${errorMessage}`);
      setIsInitialized(false);
    }
  }, [facingMode, stopCamera]);

  React.useEffect(() => {
    let mounted = true;

    const initCamera = async () => {
      if (mounted && open && showCamera) {
        await startCamera();
      }
    };

    initCamera();

    return () => {
      mounted = false;
      stopCamera();
    };
  }, [facingMode, startCamera, stopCamera, open, showCamera]);

  useEffect(() => {
    let isMounted = true;
    async function fetchSignedUrl() {
      if (photoPreview && !photoPreview.startsWith('data:')) {
        const { data, error } = await supabase.storage.from('lost-and-found-photos').createSignedUrl(photoPreview, 60 * 60);
        if (isMounted && data?.signedUrl) setSignedPhotoUrl(data.signedUrl);
      } else {
        setSignedPhotoUrl(null);
      }
    }
    fetchSignedUrl();
    return () => { isMounted = false; };
  }, [photoPreview]);

  const cleanup = React.useCallback(() => {
    stopCamera();
    if (fileReaderRef.current) {
      fileReaderRef.current.abort();
      fileReaderRef.current = null;
    }
  }, [stopCamera]);

  React.useEffect(() => {
    if (!open) {
      cleanup();
    }
  }, [open, cleanup]);

  React.useEffect(() => {
    return () => {
      cleanup();
    };
  }, [cleanup]);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];

      // Validate file type
      if (!ALLOWED_FILE_TYPES.includes(file.type)) {
        toast.error(`Invalid file type. Allowed types: ${ALLOWED_FILE_TYPES.map(type => type.split('/')[1]).join(', ')}`);
        return;
      }

      // Validate file size
      if (file.size > MAX_FILE_SIZE) {
        toast.error(`File size exceeds ${MAX_FILE_SIZE / (1024 * 1024)}MB limit`);
        return;
      }

      setPhotoFile(file);
      try {
        if (fileReaderRef.current) {
          fileReaderRef.current.abort();
        }
        const reader = new FileReader();
        fileReaderRef.current = reader;
        reader.onloadend = () => {
          if (fileReaderRef.current === reader) {
            setPhotoPreview(reader.result as string);
            toast.success('Image processed successfully');
          }
        };
        reader.onerror = () => {
          toast.error('Failed to read file');
          setError('Failed to read file');
        };
        reader.readAsDataURL(file);
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Failed to process image';
        toast.error(message);
        setError('Failed to process image');
      }
    }
  };

  const handleCameraAction = React.useCallback(() => {
    if (showCamera && isInitialized) {
      try {
        const canvas = document.createElement('canvas');
        const video = videoRef.current;
        if (!video) return;
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        const context = canvas.getContext('2d');
        if (!context) {
          throw new Error('Failed to get canvas context');
        }
        if (facingMode === 'user') {
          context.translate(canvas.width, 0);
          context.scale(-1, 1);
        }
        context.drawImage(video, 0, 0);
        const dataUrl = canvas.toDataURL('image/jpeg', 0.9);
        setPhotoPreview(dataUrl);
        setShowCamera(false);
        toast.success('Photo captured successfully');
        // Clean up canvas context
        context.clearRect(0, 0, canvas.width, canvas.height);
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Failed to capture image';
        setError(message);
        toast.error(message);
      }
    } else {
      setShowCamera(true);
    }
  }, [showCamera, isInitialized, facingMode]);

  const toggleCamera = React.useCallback(() => {
    setFacingMode(prev => prev === 'environment' ? 'user' : 'environment');
  }, []);

  const handleSave = async () => {
    if (photoPreview) {
      try {
        // Upload to Supabase Storage
        const filePath = await uploadImage(photoPreview, itemId);
        if (filePath) {
          onSave(filePath);
          toast.success('Photo uploaded successfully');

          // Run OCR on the uploaded image ONLY if enabled
          if (enableOcr) {
            const ocrResult = await runOcr(filePath);
            console.log('🧾 OCR result:', ocrResult);
            console.log('🧾 OCR result text:', ocrResult.text, 'Type:', typeof ocrResult.text, 'Length:', ocrResult.text?.length);

            const textToSend = typeof ocrResult.text === 'string' ? ocrResult.text.trim() : '';
            console.log('🧾 textToSend:', textToSend, 'Length:', textToSend.length, 'Is truthy:', !!textToSend);

            if (textToSend.length > 0) {
              // Log before sending to OpenAI
              console.log('📤 Sending to OpenAI:', {
                truncatedText: textToSend.slice(0, 200), // Limit output to avoid overload
              });
              // Call OpenAI extraction on the OCR text
              const extracted = await extractLabelFields(textToSend);
              // Log after receiving OpenAI response
              console.log('📥 OpenAI response parsed:', {
                guestName: extracted.guest_name,
                trackingNumber: extracted.tracking_number,
              });
              toast.success(`Extracted: ${JSON.stringify(extracted)}`);

              // Create a new package row in Supabase
              if (extracted.guest_name && extracted.tracking_number) {
                await createPackage({
                  guest_name: extracted.guest_name,
                  tracking: extracted.tracking_number,
                  room_number: '',
                });
                toast.success('Package log created!');
              } else {
                toast.error('Could not extract all required fields from label.');
              }
            } else {
              toast.error('OCR did not return any usable text');
            }
          }
        }
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Failed to upload photo';
        toast.error(message);
        setError(message);
        return;
      }
    }
    onClose();
  };

  const handleRetry = () => {
    setError(null);
    setShowCamera(true);
    startCamera();
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-xs">
        <DialogHeader>
          <DialogTitle>Item Photo</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4">
          {error ? (
            <div className="p-4 text-center">
              <p className="text-destructive mb-4">{error}</p>
              <Button 
                variant="outline" 
                onClick={handleRetry}
                className="mt-2"
              >
                Retry Camera
              </Button>
            </div>
          ) : (
            <>
              <div className="relative">
                {showCamera ? (
                  <>
                    <video
                      ref={videoRef}
                      autoPlay
                      playsInline
                      muted
                      className="w-full h-64 object-cover rounded-lg"
                      aria-label="Camera preview"
                    />
                    {isMobile && (
                      <Button 
                        variant="outline" 
                        onClick={toggleCamera}
                        className="absolute top-2 right-2"
                        aria-label="Switch camera"
                      >
                        <RotateCcw className="h-4 w-4" />
                      </Button>
                    )}
                  </>
                ) : photoPreview ? (
                  <img 
                    src={photoPreview.startsWith('data:') ? photoPreview : signedPhotoUrl || ''} 
                    alt="Preview" 
                    className="rounded shadow max-h-56 mb-2 w-full object-cover" 
                  />
                ) : (
                  <div className="w-full h-64 bg-muted flex flex-col items-center justify-center rounded-lg">
                    <Camera className="h-12 w-12 text-muted-foreground opacity-50" />
                    <span className="text-sm text-muted-foreground mt-2">No photo selected</span>
                  </div>
                )}
              </div>
              
              <div className="flex gap-2">
                <Button 
                  onClick={handleCameraAction} 
                  className="flex-1"
                  disabled={showCamera && !isInitialized || isUploading}
                  aria-label={showCamera ? 'Take photo' : 'Use camera'}
                >
                  {showCamera ? (
                    <>
                      <Camera className="mr-2 h-4 w-4" />
                      Take Photo
                    </>
                  ) : (
                    <>
                      <Camera className="mr-2 h-4 w-4" />
                      Use Camera
                    </>
                  )}
                </Button>

                <input
                  id="photoUploadModal"
                  type="file"
                  accept={ALLOWED_FILE_TYPES.join(',')}
                  className="hidden"
                  onChange={handleFileChange}
                  aria-label="Upload photo"
                />
                <Button
                  className="flex-1"
                  variant="outline"
                  type="button"
                  onClick={() => {
                    setShowCamera(false);
                    document.getElementById('photoUploadModal')?.click();
                  }}
                  disabled={isUploading}
                  aria-label="Upload photo from device"
                >
                  <Upload className="mr-2 h-4 w-4" />
                  Upload
                </Button>
              </div>

              {isMobile && showCamera && (
                <div className="text-center text-xs text-muted-foreground">
                  <p>Tip: Hold your device steady and ensure good lighting</p>
                </div>
              )}
            </>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isUploading}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={!photoPreview || isUploading}>
            {isUploading ? (
              <>
                <LoadingSpinner className="mr-2 h-4 w-4" />
                Uploading...
              </>
            ) : (
              'Save'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default PhotoUploadModal;
